"use client";

import { CheckCircle2, Circle, Info, ShieldCheck, Satellite, Wrench } from "lucide-react";
import { formatPrice, parsePrice } from "@/utils/format-price";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter,
    DialogClose,
} from "@/components/ui/dialog";

export interface DispositiviSicurezzaProps {
    devices: any[];
    selected: any[];
    pagamento: "finanziamento" | "cash";
    isKmLow: boolean;
    onChange: (selected: any[]) => void;
}

export const DispositiviSicurezza = ({
    devices,
    selected,
    pagamento,
    isKmLow,
    onChange,
}: DispositiviSicurezzaProps) => {
    const handleDispositivoClick = (dev: any) => {
        const isGhost = dev.title.toLowerCase().includes('targa');
        const isSelected = selected.some((d: any) => d.id === dev.id);
        let newSelected = [...selected];

        if (isSelected) {
            newSelected = newSelected.filter((d: any) => d.id !== dev.id);
        } else {
            if (isGhost) {
                newSelected = newSelected.filter((d: any) => !d.title.toLowerCase().includes('targa'));
                newSelected.push(dev);
            } else {
                newSelected = newSelected.filter((d: any) => d.title.toLowerCase().includes('targa'));
                newSelected.push(dev);
            }
        }
        onChange(newSelected);
    };

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <h4 className="font-semibold text-gray-900 text-sm">
                    Dispositivo Antifurto
                </h4>
                <Dialog>
                    <DialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700 p-0 h-auto text-xs font-medium gap-1">
                            <Info size={13} />
                            Info
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle className="text-blue-600 text-xl font-bold">Dispositivi Antifurto</DialogTitle>
                            <DialogDescription className="pt-4 text-gray-700 space-y-3">
                                <p>I dispositivi antifurto sono obbligatori per attivare la polizza assicurativa Furto e Incendio.</p>
                                <div className="space-y-2">
                                    {devices.map((dev: any) => {
                                        const isGhost = dev.title.toLowerCase().includes('targa');
                                        const price = parsePrice(dev.price);
                                        let desc = "Dispositivo meccanico.";
                                        if (isGhost) desc = "Dispositivo satellitare GPS.";
                                        else if (dev.title.toLowerCase().includes('blockshaft')) desc = "Dispositivo meccanico avanzato con protezione albero motore.";
                                        else if (dev.title.toLowerCase().includes('euro')) desc = "Dispositivo meccanico di blocco sterzo.";
                                        else if (dev.description) desc = dev.description;
                                        return (
                                            <p key={dev.id}><strong>{dev.title} ({formatPrice(price)})</strong> - {desc}</p>
                                        );
                                    })}
                                    <p className="text-sm text-blue-600 italic pt-2">Scegliendo l&apos;antifurto satellitare hai diritto ad uno sconto sulla polizza assicurativa!</p>
                                </div>
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter className="sm:justify-center mt-4">
                            <DialogClose asChild>
                                <Button type="button" className="bg-blue-600 text-white hover:bg-blue-700 px-8">Ok</Button>
                            </DialogClose>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="grid grid-cols-3 gap-3">
                {devices.map((dev: any) => {
                    const isGhost = dev.title.toLowerCase().includes('targa');
                    const price = parsePrice(dev.price);
                    const isSelected = selected.some((d: any) => d.id === dev.id);
                    const isFree = isKmLow && isGhost;

                    return (
                        <div
                            key={dev.id}
                            onClick={() => {
                                if (isFree) return;
                                handleDispositivoClick(dev);
                            }}
                            className={`relative rounded-2xl border-2 p-4 transition-all duration-200 flex flex-col gap-3 ${isSelected
                                    ? "border-blue-600 bg-blue-50/50 shadow-sm"
                                    : "border-gray-200 bg-white hover:border-gray-300"
                                } ${isFree ? "cursor-default" : "cursor-pointer"}`}
                        >
                            {isGhost && (
                                <div className="absolute -top-2.5 left-4 bg-blue-600 text-white text-[9px] font-bold px-2 py-0.5 rounded-full flex items-center gap-0.5">
                                    <ShieldCheck className="w-2.5 h-2.5" />
                                    SCONTO POLIZZA
                                </div>
                            )}
                            <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${isSelected ? "bg-blue-100" : "bg-gray-100"}`}>
                                {isGhost
                                    ? <Satellite size={17} className={isSelected ? "text-blue-600" : "text-gray-400"} />
                                    : <Wrench size={17} className={isSelected ? "text-blue-600" : "text-gray-400"} />
                                }
                            </div>
                            <div className="flex-1 min-w-0">
                                <span className="font-bold text-gray-900 text-sm block leading-tight">{dev.title}</span>
                                <span className="text-xs text-gray-500">{isGhost ? "Satellitare GPS" : "Meccanico"}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className={`font-bold ${isFree ? (pagamento === "finanziamento" ? "text-sm text-blue-600" : "text-[11px] text-blue-600 leading-tight") : "text-sm text-gray-900"}`}>
                                    {isFree ? (pagamento === "finanziamento" ? "GRATIS" : "Incluso nel Pacchetto Sicurezza") : formatPrice(price)}
                                </span>
                                {isSelected
                                    ? <CheckCircle2 className="h-5 w-5 text-blue-600 shrink-0" />
                                    : <Circle className="h-5 w-5 text-gray-300 shrink-0" />
                                }
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
